<template>
  <v-card
    dark
    class="gesture-alerts-card d-flex flex-column"
    elevation="2"
  >
    <v-card-title class="py-2 px-3 subtitle-2 font-weight-bold d-flex align-center flex-shrink-0 gesture-card-title-line">
      <v-icon left small color="white" class="flex-shrink-0">mdi-hand-back-right-outline</v-icon>
      <span class="gesture-card-title-text">Gestures Alerts Timeline</span>
      <v-spacer />
      <v-btn
        icon
        small
        aria-label="Expand or collapse Gestures Alerts Timeline panel"
        @click="$emit('update:collapsed', !collapsed)"
      >
        <v-icon small>{{ collapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
      </v-btn>
      <v-menu v-model="settingsMenuOpen" offset-y left :close-on-content-click="false">
        <template #activator="{ on, attrs }">
          <v-btn icon small v-bind="attrs" v-on="on" aria-label="Open gesture timeline settings">
            <v-icon small>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-card class="gesture-menu pa-3" width="260">
          <!-- Action: gestures-by-typology dialog (menu keeps itself open on content
               clicks, so the handler closes it explicitly). -->
          <template v-if="sourceType && sourceId">
            <v-list-item
              dense
              class="px-0"
              aria-label="Show gestures by typology"
              @click="openTypologyFromMenu"
            >
              <v-list-item-icon class="mr-2 my-2">
                <v-icon small>mdi-counter</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>Gestures by typology</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
            <v-divider class="mb-2" />
          </template>

          <!-- Filter: alert reason -->
          <div class="subtitle-2 font-weight-bold mb-1">Alert reason</div>
          <v-divider class="mb-2" />
          <v-checkbox
            v-model="reasonFilters.above"
            label="Above baseline"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="reasonFilters.below"
            label="Below baseline"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="reasonFilters.rare"
            label="Rare appearance"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="reasonFilters.at_baseline"
            label="At baseline"
            hide-details
            dense
            class="mt-0 pt-0"
          />

          <!-- Filter: habituality analysis method -->
          <div class="subtitle-2 font-weight-bold mt-4 mb-1">Analysis method</div>
          <v-divider class="mb-2" />
          <v-checkbox
            v-model="methodFilters.absolute"
            label="Absolute"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="methodFilters.baseline"
            label="Baseline"
            hide-details
            dense
            class="mt-0 pt-0"
          />

          <!-- Filter: discarded rows (shown muted inline in the list/timeline, never analyzed) -->
          <div class="subtitle-2 font-weight-bold mt-4 mb-1">Discarded</div>
          <v-divider class="mb-2" />
          <v-checkbox
            v-model="discardedFilters.sustained_not_valid"
            label="Sustained — not valid"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="discardedFilters.habitual"
            label="Habitual"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="discardedFilters.at_baseline"
            label="At baseline"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="discardedFilters.suppressed_subject_speech"
            label="Suppressed while speaking"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="discardedFilters.too_short"
            label="Too short (under 200 ms)"
            hide-details
            dense
            class="mt-0 pt-0"
          />

          <!-- Filter: speaking context -->
          <div class="subtitle-2 font-weight-bold mt-4 mb-1">Speaker context</div>
          <v-divider class="mb-2" />
          <v-checkbox
            v-model="speakingFilters.speaking"
            label="While speaking"
            hide-details
            dense
            class="mt-0 pt-0"
          />
          <v-checkbox
            v-model="speakingFilters.non_speaking"
            label="While not speaking"
            hide-details
            dense
            class="mt-0 pt-0"
          />

          <!-- Existing landmark / pause toggles preserved below -->
          <div class="subtitle-2 font-weight-bold mt-4 mb-1">Overlay</div>
          <v-divider class="mb-2" />
          <v-switch
            :input-value="facialLandmarksEnabled"
            color="primary"
            hide-details
            dense
            class="mt-0 pt-0"
            label="Facial landmarks"
            @change="$emit('update:facialLandmarksEnabled', !!$event)"
          />
          <v-switch
            :input-value="handLandmarksEnabled"
            color="primary"
            hide-details
            dense
            class="mt-0 pt-0"
            label="Hand landmarks"
            @change="$emit('update:handLandmarksEnabled', !!$event)"
          />
          <v-switch
            :input-value="pauseOnRedAlertsEnabled"
            color="error"
            hide-details
            dense
            class="mt-0 pt-0"
            label="Pause on red alerts"
            @change="$emit('update:pauseOnRedAlertsEnabled', !!$event)"
          />
        </v-card>
      </v-menu>
      <span v-if="!currentOnly" class="caption gesture-meta gesture-meta--ellipsis">
        {{ `${totalSignals} signals` }}
      </span>
    </v-card-title>

    <v-divider v-if="!collapsed && !currentOnly" />

    <v-card-text v-if="!collapsed && !currentOnly" class="pa-3 flex-shrink-0">
      <div class="gesture-timeline-legend caption mb-1 d-flex align-center flex-wrap">
        <span class="gesture-legend-dot gesture-legend-dot--alerted mr-1"></span>
        <span class="mr-3">Above / Below / Rare</span>
        <span class="gesture-legend-dot gesture-legend-dot--baseline mr-1"></span>
        <span class="mr-3">At baseline</span>
        <span class="gesture-legend-dot gesture-legend-dot--muted mr-1"></span>
        <span>Not an alert</span>
      </div>
      <div
        ref="timelineTrack"
        class="gesture-timeline-track"
        :class="{ 'gesture-timeline-track--picking': pickActive }"
        @click="onTrackBackgroundClick"
      >
        <div class="gesture-timeline-line"></div>
        <button
          v-for="alert in groupedAlerts"
          :key="`marker-${alert.key}`"
          class="gesture-marker-btn"
          :class="[
            `gesture-marker-btn--${alert.tone}`,
            { 'gesture-marker-btn--active': activeAlertKey === alert.key },
          ]"
          :style="{ left: `${alert.positionPercent}%` }"
          :title="alert.tooltip"
          @click="onAlertActivate(alert)"
        >
          <span class="gesture-marker-dot"></span>
        </button>
      </div>
    </v-card-text>

    <v-divider v-if="!collapsed" />

    <v-card-text
      v-if="!collapsed"
      class="pa-2 flex-grow-1 d-flex flex-column"
      style="min-height: 0;"
    >
      <div
        ref="alertListScroll"
        class="gesture-alerts-scroll"
      >
        <div v-if="!groupedAlerts.length" class="gesture-empty caption pa-6 text-center">
          No gesture detections.
        </div>
        <div v-else-if="currentOnly" class="gesture-alerts-list">
          <button
            v-if="currentOnlyAlert"
            type="button"
            class="gesture-alert-item gesture-alert-item--active"
            :class="`gesture-alert-item--${currentOnlyAlert.tone}`"
            @click="onAlertActivate(currentOnlyAlert)"
          >
            <div class="gesture-alert-item__top">
              <span class="gesture-alert-time">{{ currentOnlyAlert.time }}</span>
            </div>
            <div class="gesture-alert-description gesture-alert-description--current-only">
              {{ currentOnlyAlert.description }}
            </div>
          </button>
          <div v-else class="gesture-empty caption pa-4 text-center">
            Waiting for current gesture...
          </div>
        </div>
        <div v-else class="gesture-alerts-list">
          <div
            v-for="alert in groupedAlerts"
            :key="`row-${alert.key}`"
            ref="alertItemEl"
            class="gesture-alert-item"
            :class="[
              `gesture-alert-item--${alert.tone}`,
              { 'gesture-alert-item--active': activeAlertKey === alert.key },
              { 'gesture-alert-item--feedback-correct': cardVerdict(alert) === 'correct' },
              { 'gesture-alert-item--feedback-incorrect': cardVerdict(alert) === 'incorrect' },
            ]"
          >
            <!-- HEADER: [count] [timing centered] [info button] -->
            <div class="gesture-alert-item__header">
              <span class="gesture-alert-count">{{ alert.subAlerts.length }}</span>
              <v-spacer />
              <div class="gesture-alert-item__timing">
                <span class="gesture-alert-time">{{ alert.time }}</span>
                <span v-if="alert.durationMs" class="gesture-alert-duration">
                  {{ formatDuration(alert.durationMs) }}
                </span>
                <v-icon
                  v-if="alert.isSpeaking === true"
                  x-small
                  title="While speaking"
                >mdi-microphone</v-icon>
                <v-icon
                  v-else-if="alert.isSpeaking === false"
                  x-small
                  title="While not speaking"
                >mdi-microphone-off</v-icon>
              </div>
              <v-spacer />
              <v-btn
                icon
                x-small
                :aria-label="`Show details for ${alert.description}`"
                @click.stop="openDetailDialog(alert)"
              >
                <v-icon small>mdi-information-outline</v-icon>
              </v-btn>
            </div>

            <!-- BODY: one row per signal = [gesture name] [reason chip] -->
            <div
              class="gesture-alert-item__body"
              role="button"
              tabindex="0"
              @click="onAlertActivate(alert)"
              @keydown.enter.prevent="onAlertActivate(alert)"
            >
              <div
                v-for="(sub, idx) in alert.subAlerts"
                :key="sub.id || idx"
                class="gesture-alert-signal-row"
                :class="{ 'gesture-alert-signal-row--muted': sub.analysis_status !== 'alert' }"
              >
                <span class="gesture-alert-signal-name">{{ sub.gesture_name }}</span>
                <v-chip
                  v-if="subStatusBadge(sub)"
                  x-small
                  :dark="sub.analysis_status === 'alert'"
                  :color="sub.analysis_status === 'alert' ? 'orange darken-2' : undefined"
                  :outlined="sub.analysis_status !== 'alert'"
                  class="gesture-status-badge"
                >{{ subStatusBadge(sub) }}</v-chip>
                <v-tooltip bottom :disabled="!subMethodTooltip(sub)">
                  <template #activator="{ on, attrs }">
                    <v-chip
                      v-if="subMethodChip(sub)"
                      x-small
                      v-bind="attrs"
                      :dark="subMethodChipDark(sub)"
                      :color="subMethodChipColor(sub)"
                      :text-color="subMethodChipDark(sub) ? undefined : 'black'"
                      v-on="on"
                    >{{ subMethodChip(sub) }}</v-chip>
                  </template>
                  <span>{{ subMethodTooltip(sub) }}</span>
                </v-tooltip>
              </div>
            </div>

            <!-- FOOTER: [correct] [incorrect] -->
            <div class="gesture-alert-item__footer debug-actions">
              <v-btn
                x-small
                depressed
                :color="cardVerdict(alert) === 'correct' ? 'success' : 'transparent'"
                :class="['gesture-vote-btn', cardVerdict(alert) === 'correct' ? 'gesture-vote-btn--active-correct' : 'gesture-vote-btn--inactive']"
                :aria-label="`Mark gesture ${alert.description} as correct`"
                @click.stop="markGesture(alert, 'correct')"
              >
                <v-icon x-small left>mdi-check</v-icon>
                Correct
              </v-btn>
              <v-btn
                x-small
                depressed
                :color="cardVerdict(alert) === 'incorrect' ? 'error' : 'transparent'"
                :class="['gesture-vote-btn', cardVerdict(alert) === 'incorrect' ? 'gesture-vote-btn--active-incorrect' : 'gesture-vote-btn--inactive']"
                :aria-label="`Mark gesture ${alert.description} as incorrect`"
                @click.stop="markGesture(alert, 'incorrect')"
              >
                <v-icon x-small left>mdi-close</v-icon>
                Incorrect
              </v-btn>
            </div>
          </div>
        </div>

        <!-- Discarded gestures: per-reason count summary. The rows themselves
             are rendered muted inline in the main list and timeline. -->
        <div v-if="!currentOnly && discardedTotal > 0" class="gesture-discarded-section mt-2">
          <button
            type="button"
            class="gesture-discarded-toggle d-flex align-center"
            :aria-expanded="discardedSectionOpen ? 'true' : 'false'"
            aria-label="Expand or collapse discarded gestures"
            @click="discardedSectionOpen = !discardedSectionOpen"
          >
            <v-icon x-small class="mr-1">{{ discardedSectionOpen ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            <span class="caption font-weight-bold">Discarded gestures ({{ discardedTotal }})</span>
          </button>
          <div v-if="discardedSectionOpen" class="gesture-discarded-summary d-flex flex-wrap mt-1">
            <v-chip
              v-for="item in discardedSummary"
              :key="`ds-${item.reason}`"
              x-small
              outlined
              class="gesture-discarded-badge"
            >{{ item.label }}: {{ item.count }}</v-chip>
          </div>
          <div v-if="discardedSectionOpen" class="caption gesture-discarded-hint mt-1">
            These detections appear dimmed in the list and timeline above.
          </div>
        </div>
      </div>
    </v-card-text>

    <v-divider v-if="!collapsed && sourceType && sourceId && $vuetify.breakpoint.mdAndUp" />
    <div
      v-if="!collapsed && sourceType && sourceId && $vuetify.breakpoint.mdAndUp"
      class="gesture-timeline-footer pa-2"
    >
      <v-spacer />
      <v-tooltip top>
        <template #activator="{ on, attrs }">
          <v-btn
            icon
            small
            :color="reportMissingActive ? 'warning' : 'white'"
            :aria-label="reportMissingActive ? 'Cancel report missing gesture' : 'Report missing gesture'"
            v-bind="attrs"
            v-on="on"
            @click="startReportMissing"
          >
            <v-icon small color="white">{{ reportMissingActive ? 'mdi-close' : 'mdi-flag-plus-outline' }}</v-icon>
          </v-btn>
        </template>
        <span>{{ reportMissingActive ? 'Cancel — click timeline to set timestamp' : 'Report missing gesture' }}</span>
      </v-tooltip>
    </div>

    <AlertFeedbackDialog
      v-if="sourceType && sourceId"
      v-model="feedbackDialogOpen"
      mode="gesture"
      :source-type="sourceType"
      :source-id="sourceId"
      :target="feedbackTarget"
      :alerts="feedbackAlerts"
      @saved="onGestureFeedbackSaved"
    />

    <AlertFeedbackDialog
      v-if="sourceType && sourceId"
      v-model="missingDialogOpen"
      mode="missing_gesture"
      :source-type="sourceType"
      :source-id="sourceId"
      :missing-timestamp-ms="missingPendingTimestampMs"
      @saved="onMissingSaved"
      @cancel="onMissingCancel"
      @input="(v) => { if (!v) onMissingCancel(); }"
    />

    <GestureTypologyDialog
      v-if="sourceType && sourceId"
      v-model="typologyDialogOpen"
      :source-type="sourceType"
      :source-id="sourceId"
    />

    <!-- Detail dialog (info button): one section per underlying gesture detection -->
    <v-dialog v-model="detailDialogOpen" max-width="640" scrollable>
      <v-card v-if="detailAlert" light class="gesture-detail-dialog-card">
        <v-card-title class="subtitle-1 font-weight-bold pr-8">
          Gesture details
          <v-spacer />
          <v-btn icon small @click="detailDialogOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-card-subtitle class="pb-0 pt-1">
          <v-chip x-small outlined class="mr-1">{{ detailAlert.time }}</v-chip>
          <span class="caption grey--text">
            {{ detailAlertSubAlerts.length }} {{ detailAlertSubAlerts.length === 1 ? 'detection' : 'detections' }} at this moment
          </span>
        </v-card-subtitle>
        <v-card-text class="pt-4">
          <v-expansion-panels
            v-model="detailExpandedPanels"
            accordion
            multiple
            flat
            class="gesture-detail-panels"
          >
            <v-expansion-panel
              v-for="(sub, idx) in detailAlertSubAlerts"
              :key="sub.id || idx"
              class="gesture-detail-sub-panel"
            >
              <v-expansion-panel-header class="gesture-detail-sub-header py-2">
                <div class="d-flex align-center flex-wrap" style="gap: 6px;">
                  <span class="subtitle-2 font-weight-bold">{{ sub.gesture_name }}</span>
                  <v-chip
                    v-if="subMethodChip(sub)"
                    x-small
                    :dark="subMethodChipDark(sub)"
                    :color="subMethodChipColor(sub)"
                    :text-color="subMethodChipDark(sub) ? undefined : 'black'"
                    :title="subMethodTooltip(sub) || undefined"
                  >{{ subMethodChip(sub) }}</v-chip>
                  <v-chip
                    v-if="subStatusBadge(sub)"
                    x-small
                    :dark="sub.analysis_status === 'alert'"
                    :color="sub.analysis_status === 'alert' ? 'orange darken-2' : undefined"
                    :outlined="sub.analysis_status !== 'alert'"
                  >{{ subStatusBadge(sub) }}</v-chip>
                  <v-chip
                    v-if="sub.is_speaking === true"
                    x-small
                    outlined
                  ><v-icon x-small left>mdi-microphone</v-icon>While speaking</v-chip>
                  <v-chip
                    v-else-if="sub.is_speaking === false"
                    x-small
                    outlined
                  ><v-icon x-small left>mdi-microphone-off</v-icon>Not speaking</v-chip>
                  <v-chip
                    v-if="gestureFeedback[sub.id] === 'correct'"
                    x-small
                    dark
                    color="success"
                  ><v-icon x-small left>mdi-check</v-icon>Correct</v-chip>
                  <v-chip
                    v-else-if="gestureFeedback[sub.id] === 'incorrect'"
                    x-small
                    dark
                    color="error"
                  ><v-icon x-small left>mdi-close</v-icon>Incorrect</v-chip>
                  <span v-if="sub.duration_ms" class="caption grey--text ml-auto">
                    {{ formatDuration(sub.duration_ms) }}
                  </span>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content class="gesture-detail-sub-content">
                <div v-if="subAlertRows(sub).length" class="alert-detail-grid caption">
                  <template v-for="(group, gi) in subAlertRows(sub)">
                    <div
                      :key="`group-${idx}-${gi}`"
                      class="alert-detail-group-title overline"
                    >{{ group.title }}</div>
                    <div
                      v-for="(row, ri) in group.rows"
                      :key="`row-${idx}-${gi}-${ri}`"
                      class="alert-detail-row"
                    >
                      <span class="alert-detail-label">
                        {{ row.label }}
                        <v-tooltip v-if="row.description" bottom max-width="320">
                          <template #activator="{ on, attrs }">
                            <v-icon
                              v-bind="attrs"
                              x-small
                              class="alert-detail-info"
                              tabindex="0"
                              v-on="on"
                            >mdi-information-outline</v-icon>
                          </template>
                          <span>{{ row.description }}</span>
                        </v-tooltip>
                      </span>
                      <span class="alert-detail-value">{{ row.value }}</span>
                    </div>
                  </template>
                </div>
                <div v-else class="body-2 grey--text">No additional metadata.</div>

                <div v-if="sub.explain && sub.explain.notes" class="body-2 mt-2">
                  {{ sub.explain.notes }}
                </div>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn text small color="primary" @click="seekFromDetailDialog">
            <v-icon left x-small>mdi-play-circle-outline</v-icon>
            Jump to moment
          </v-btn>
          <v-spacer />
          <v-btn text @click="detailDialogOpen = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import AlertFeedbackDialog from './AlertFeedbackDialog.vue';
import GestureTypologyDialog from './GestureTypologyDialog.vue';
import DetectionFeedbackService from '../../services/DetectionFeedbackService';
import {
  buildGestureMethodIndex,
  lookupGestureMethodInfo,
  methodChipLabel,
  discardReasonLabel,
} from '../../utils/gestureAnalysisUtils';

const STORAGE_PREFIX = 'vp.gestureAlertsTimeline';

function loadPersistedFilters(key, defaults) {
  try {
    if (typeof localStorage === 'undefined') return { ...defaults };
    const raw = localStorage.getItem(`${STORAGE_PREFIX}.${key}`);
    if (!raw) return { ...defaults };
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') {
      // Merge with defaults so newly added keys appear with their default value.
      return { ...defaults, ...parsed };
    }
  } catch (e) {
    // ignore corrupted entries
  }
  return { ...defaults };
}

function savePersistedFilters(key, value) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(`${STORAGE_PREFIX}.${key}`, JSON.stringify(value));
  } catch (e) {
    // ignore quota errors
  }
}

export default {
  name: 'GestureAlertsTimeline',

  components: { AlertFeedbackDialog, GestureTypologyDialog },

  props: {
    gestureData: {
      type: Array,
      default: () => [],
    },
    videoDuration: {
      type: Number,
      default: 0,
    },
    currentVideoTime: {
      type: Number,
      default: 0,
    },
    facialLandmarksEnabled: {
      type: Boolean,
      default: true,
    },
    handLandmarksEnabled: {
      type: Boolean,
      default: true,
    },
    pauseOnRedAlertsEnabled: {
      type: Boolean,
      default: false,
    },
    collapsed: {
      type: Boolean,
      default: false,
    },
    currentOnly: {
      type: Boolean,
      default: false,
    },
    /**
     * When true, clicking the track does not jump video time; instead emits
     * `pick-timestamp` with the seconds where the user clicked. Used by the
     * "Add missing alert" flow in the feedback dialog.
     */
    pickModeEnabled: {
      type: Boolean,
      default: false,
    },
    sourceType: {
      type: String,
      default: null, // 'interview' | 'manual_analysis'
    },
    sourceId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      gestureFeedback: {}, // { gestureKey: 'correct'|'incorrect' }
      feedbackDialogOpen: false,
      feedbackTarget: null,
      feedbackAlerts: [],
      // Filter keys carry a .v2 suffix: the timeline now shows ALL detections
      // inline (alerts + discarded), so every filter defaults to visible and
      // stale persisted hides from the v1 layout are ignored.
      reasonFilters: loadPersistedFilters('reasonFilters.v2', {
        above: true,
        below: true,
        rare: true,
        at_baseline: true,
      }),
      speakingFilters: loadPersistedFilters('speakingFilters.v2', {
        speaking: true,
        non_speaking: true,
      }),
      // Habituality analysis method of each gesture (absolute frequency bands
      // vs personal baseline deviation). Old analyses count as 'baseline'.
      methodFilters: loadPersistedFilters('methodFilters.v2', {
        absolute: true,
        baseline: true,
      }),
      // Which discard reasons stay visible in the main list and timeline.
      // Discarded rows render muted for transparency but are never analyzed.
      discardedFilters: loadPersistedFilters('discardedFilters.v2', {
        sustained_not_valid: true,
        habitual: true,
        at_baseline: true,
        suppressed_subject_speech: true,
        too_short: true,
      }),
      discardedSectionOpen: false,
      // Report missing-gesture flow (D-T8 / A-T6 reworked):
      // 1. user clicks "Report missing gesture" → reportMissingActive=true, banner shows
      // 2. user clicks on timeline → onTrackBackgroundClick → emits + sets pending ms
      // 3. dialog opens in missing_gesture mode with that timestamp pinned
      reportMissingActive: false,
      missingDialogOpen: false,
      missingPendingTimestampMs: null,
      settingsMenuOpen: false,
      typologyDialogOpen: false,
      detailDialogOpen: false,
      detailAlert: null,
      // Indexes of the expanded sub-alert panels inside the detail dialog.
      // Reset every time the dialog opens so detections start collapsed and
      // only expand on demand — keeps the dialog short when there are many
      // detections at the same moment.
      detailExpandedPanels: [],
    };
  },

  mounted() {
    this.$root.$on('vp:cancel-report-missing', this.onMissingCancel);
    this.loadExistingFeedback();
  },

  beforeDestroy() {
    this.$root.$off('vp:cancel-report-missing', this.onMissingCancel);
  },

  computed: {
    /** Crosshair is active when the parent enables pickModeEnabled (legacy)
     *  OR when the internal "Report missing gesture" flow is awaiting a click. */
    pickActive() {
      return this.pickModeEnabled || (this.reportMissingActive && !this.missingDialogOpen);
    },
    detailAlertSubAlerts() {
      return Array.isArray(this.detailAlert?.subAlerts) ? this.detailAlert.subAlerts : [];
    },
    effectiveDuration() {
      if (Number.isFinite(this.videoDuration) && this.videoDuration > 0) {
        return this.videoDuration;
      }
      const source = this.$store.getters['gestureAlerts/getFilteredAlerts'];
      if (!Array.isArray(source) || !source.length) return 0;
      let maxSeconds = 0;
      for (let i = 0; i < source.length; i += 1) {
        const sec = this.parseTimeToSeconds(source[i]?.time);
        if (Number.isFinite(sec) && sec > maxSeconds) maxSeconds = sec;
      }
      return maxSeconds > 0 ? maxSeconds + 1 : 0;
    },

    /**
     * Raw gesture_results doc lookup for the new habituality fields.
     * The shaped rows in gestureAlerts/getFilteredAlerts are stripped to a
     * field whitelist (analysisData.fetchGestures), so analysis_method /
     * analysis_status / discard_reason / frequency_band / absolute_count are
     * re-joined here via episode_id (or timestamp+gesture_name fallback).
     */
    gestureMethodIndex() {
      const analysisId = this.$store.getters['gestureAlerts/getAnalysisId'];
      const doc = analysisId != null
        ? this.$store.getters['analysisData/getGestures'](analysisId)
        : null;
      return buildGestureMethodIndex(doc);
    },

    /** Filtered store alerts enriched with the habituality method fields. */
    enrichedAlerts() {
      const source = this.$store.getters['gestureAlerts/getFilteredAlerts'];
      if (!Array.isArray(source) || !source.length) return [];
      const index = this.gestureMethodIndex;
      return source.map((a) => {
        const info = lookupGestureMethodInfo(index, {
          episodeId: a?.episode_id ?? null,
          timestampMs: Math.round(this.parseTimeToSeconds(a?.time) * 1000),
          gestureName: a?.gesture_name ?? null,
        });
        if (info) return { ...a, ...info };
        // Old analyses (schema 1.3) or unmatched rows: baseline alert defaults.
        return {
          ...a,
          analysis_method: 'baseline',
          analysis_status: 'alert',
          discard_reason: null,
          frequency_band: null,
          absolute_count: null,
          has_method_data: false,
        };
      });
    },

    /**
     * Per-reason counts of discarded rows (never analyzed). Counts are
     * unfiltered totals: the Discarded filters only control whether the rows
     * appear inline in the main list/timeline, not what the analysis dropped.
     */
    discardedSummary() {
      const counts = new Map();
      this.enrichedAlerts
        .filter((a) => a.analysis_status === 'discarded')
        .forEach((a) => {
          const reason = String(a.discard_reason || 'discarded');
          counts.set(reason, (counts.get(reason) || 0) + 1);
        });
      return Array.from(counts.entries()).map(([reason, count]) => ({
        reason,
        // Compact chip labels; the long form stays on the row badges.
        label: reason === 'suppressed_subject_speech' ? 'Suppressed' : discardReasonLabel(reason),
        count,
      }));
    },

    discardedTotal() {
      return this.discardedSummary.reduce((sum, item) => sum + item.count, 0);
    },

    groupedAlerts() {
      const source = this.enrichedAlerts;
      if (!Array.isArray(source) || !source.length) return [];
      const duration = this.effectiveDuration;
      if (!Number.isFinite(duration) || duration <= 0) return [];

      const byMs = new Map();
      source.forEach((alert) => {
        const seconds = this.parseTimeToSeconds(alert?.time);
        const keyMs = Math.round(seconds * 1000);
        if (!Number.isFinite(keyMs)) return;
        const list = byMs.get(keyMs) || [];
        list.push(alert);
        byMs.set(keyMs, list);
      });

      const reasonOf = (a) => {
        // Backend ships reason in alert.reason or alert.explain.reason. Defaults
        // to at_baseline when missing so legacy data still renders something.
        const r = String(a?.reason || a?.explain?.reason || 'at_baseline').toLowerCase();
        if (r.includes('above')) return 'above';
        if (r.includes('below')) return 'below';
        if (r.includes('rare')) return 'rare';
        return 'at_baseline';
      };
      const isSpeakingOf = (a) => {
        // Prefer explicit backend flag, fall back to explain.overlaps_subject_speech.
        if (typeof a?.is_speaking === 'boolean') return a.is_speaking;
        const explainFlag = a?.explain?.overlaps_subject_speech;
        if (typeof explainFlag === 'boolean') return explainFlag;
        return null;
      };

      const reasonFilters = this.reasonFilters;
      const speakingFilters = this.speakingFilters;
      const methodFilters = this.methodFilters;
      const discardedFilters = this.discardedFilters;
      const filteredByMs = new Map(
        Array.from(byMs.entries())
          .map(([k, list]) => {
            const kept = list.filter((a) => {
              // Discarded (non-alert) rows render muted inline in the list
              // and timeline; their visibility is governed solely by the
              // Discarded filters (default: everything visible).
              if (a?.analysis_status === 'discarded') {
                const key = String(a?.discard_reason || '').toLowerCase();
                if (Object.prototype.hasOwnProperty.call(discardedFilters, key)) {
                  return !!discardedFilters[key];
                }
                return true; // unknown discard reasons stay visible
              }
              const method = a?.analysis_method === 'absolute' ? 'absolute' : 'baseline';
              if (!methodFilters[method]) return false;
              // Baseline reason filters only apply to baseline-method rows;
              // absolute rows use frequency bands, not baseline deviations.
              if (method !== 'absolute') {
                const reason = reasonOf(a);
                if (!reasonFilters[reason]) return false;
              }
              const speaking = isSpeakingOf(a);
              if (speaking === true && !speakingFilters.speaking) return false;
              if (speaking === false && !speakingFilters.non_speaking) return false;
              return true;
            });
            return [k, kept];
          })
          .filter(([, list]) => list.length)
      );

      return Array.from(filteredByMs.entries())
        .map(([keyMs, alertsAtTime], orderIndex) => {
          const seconds = keyMs / 1000;
          const positionPercent = (seconds / duration) * 100;
          // Alert semantics (tone, active highlight, auto-pause emit) only
          // consider real alert rows; discarded rows render muted.
          // Warnings (gestures 5.0.0) are shown but are not alerts either.
          const alertRows = alertsAtTime.filter((a) => a?.analysis_status === 'alert');
          const hasAlert = alertRows.length > 0;
          const reasons = new Set(alertRows.map(reasonOf));
          const hasAlertedReason = reasons.has('above') || reasons.has('below') || reasons.has('rare');
          // Absolute-method rows that survive discarding are real alerts.
          const hasAbsoluteAlert = alertRows.some((a) => a?.analysis_method === 'absolute');
          // Orange when an alerted reason is present, yellow when only
          // at_baseline alerts, grey (muted) when every row was discarded.
          let tone = 'muted';
          if (hasAlert) tone = hasAlertedReason || hasAbsoluteAlert ? 'alerted' : 'baseline';
          const description = alertsAtTime
            .map((a) => String(a?.gesture_name || '').trim())
            .filter(Boolean)
            .join(', ');
          const time = this.formatTimeClock(seconds);
          const explain = alertsAtTime
            .map((a) => a?.explain)
            .find((e) => e && typeof e === 'object') || null;
          const gestureKind = alertsAtTime
            .map((a) => a?.gesture_kind)
            .find((k) => k === 'reaction' || k === 'spontaneous') || 'spontaneous';
          // Tooltip labels: alert rows show their baseline reason, discarded
          // rows show the human discard reason instead.
          const reasonLabels = Array.from(new Set(alertsAtTime.map((a) => (
            a?.analysis_status === 'discarded'
              ? discardReasonLabel(a?.discard_reason)
              : reasonOf(a)
          )))).join(', ');
          // Alert semantics (header chip, active-segment length) must come
          // from alert rows; co-timed discarded rows only drive muted groups.
          const durationSource = hasAlert ? alertRows : alertsAtTime;
          const durationMs = durationSource
            .map((a) => Number(a?.duration_ms ?? a?.explain?.duration_ms))
            .find((d) => Number.isFinite(d) && d > 0) || null;
          const speakingFlag = alertsAtTime
            .map(isSpeakingOf)
            .find((v) => v === true || v === false);
          // Per-sub-alert breakdown so the detail dialog can iterate each raw
          // detection. Each entry preserves the backend payload (name, reason,
          // explain, etc.) for the same timestamp. The id is the backend
          // episode_id when available, so QA feedback persists against a stable
          // identifier across reloads / reanalysis.
          const subAlerts = alertsAtTime.map((a, i) => ({
            id: String(a?.episode_id || `${keyMs}-${orderIndex}-${i}`),
            episode_id: a?.episode_id || null,
            gesture_name: String(a?.gesture_name || '—').trim() || '—',
            // Durable feedback coordinate: exact ms when the store kept it, else the
            // second-resolution group key; end = start + duration.
            start_ms: Number.isFinite(Number(a?.timestamp_ms)) ? Math.round(Number(a.timestamp_ms)) : keyMs,
            end_ms: (Number.isFinite(Number(a?.timestamp_ms)) ? Math.round(Number(a.timestamp_ms)) : keyMs)
              + (Number(a?.duration_ms) || 0),
            reason: reasonOf(a),
            gesture_kind: a?.gesture_kind || null,
            is_speaking: isSpeakingOf(a),
            duration_ms: Number(a?.duration_ms) || null,
            // Habituality method fields (engine schema >= 1.4; defaults applied
            // in enrichedAlerts for older analyses).
            analysis_method: a?.analysis_method === 'absolute' ? 'absolute' : 'baseline',
            analysis_status:
              a?.analysis_status === 'discarded' || a?.analysis_status === 'warning'
                ? a.analysis_status
                : 'alert',
            discard_reason: a?.discard_reason || null,
            frequency_band: a?.frequency_band || null,
            absolute_count:
              a?.absolute_count != null && Number.isFinite(Number(a.absolute_count))
                ? Number(a.absolute_count)
                : null,
            // False only for legacy schema-1.3 analyses: those render with no
            // status badge so old results look exactly as before.
            has_method_data: a?.has_method_data !== false,
            explain: a?.explain || null,
            // Preserve the full backend payload so the detail dialog can
            // surface any field the normalizer above didn't pick up.
            raw: a || null,
          }));
          // Card key: when the group has a single sub-alert, lock to its
          // episode_id so feedback persists across renders. Otherwise stays
          // positional (used only for Vue keys, not for feedback target_ref).
          const cardKey = subAlerts.length === 1 && subAlerts[0].episode_id
            ? subAlerts[0].episode_id
            : `${keyMs}-${orderIndex}`;
          return {
            key: cardKey,
            seconds,
            positionPercent: Math.min(100, Math.max(0, positionPercent)),
            tone,
            hasAlert,
            description: description || 'Gesture alert',
            time,
            tooltip: `${time} - ${description || 'Gesture alert'} (${reasonLabels})`,
            explain,
            gestureKind,
            reasonLabels,
            durationMs,
            isSpeaking: typeof speakingFlag === 'boolean' ? speakingFlag : null,
            subAlerts,
          };
        })
        .sort((a, b) => a.seconds - b.seconds);
    },

    totalSignals() {
      return this.groupedAlerts.reduce((sum, a) => sum + (a.subAlerts?.length || 0), 0);
    },

    activeAlertIndex() {
      const alerts = this.groupedAlerts;
      if (!alerts.length) return -1;
      const now = Number(this.currentVideoTime);
      if (!Number.isFinite(now)) return -1;
      // The gesture is "active" while the playhead sits inside its detection
      // segment [start, end]. When duration_ms is shipped by the backend we
      // honour it directly; otherwise the segment is assumed to last until
      // the next alert starts (capped so a single gesture cannot stay active
      // forever when it's the last one in the timeline).
      const maxImpliedSegmentSec = 8;
      const boundaryMargin = 0.15;
      let bestIndex = -1;
      let bestDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i < alerts.length; i += 1) {
        const alert = alerts[i];
        // Alert-semantics guard: groups holding only discarded (non-alert)
        // rows never become "active" — no auto-pause, no active highlight,
        // no active-gesture-alert-change emit for them.
        if (!alert.hasAlert) continue;
        const start = Number(alert.seconds);
        if (!Number.isFinite(start)) continue;
        const declaredDurationSec = Number.isFinite(Number(alert.durationMs))
          ? Math.max(0, Number(alert.durationMs) / 1000)
          : 0;
        let end;
        if (declaredDurationSec > 0) {
          end = start + declaredDurationSec;
        } else {
          // Implied window extends to the next REAL alert group; interleaved
          // discarded-only (muted) groups must not truncate it.
          let j = i + 1;
          while (j < alerts.length && !alerts[j].hasAlert) j += 1;
          const nextStart = Number(alerts[j]?.seconds);
          const impliedEnd = Number.isFinite(nextStart) && nextStart > start
            ? nextStart
            : start + maxImpliedSegmentSec;
          end = Math.min(impliedEnd, start + maxImpliedSegmentSec);
        }
        const isInside = now >= (start - boundaryMargin) && now <= (end + boundaryMargin);
        if (!isInside) continue;
        // When two segments overlap, prefer the one whose start is closest to
        // (but not after) the current playhead — i.e. the most recently
        // entered segment — so the highlight tracks the video naturally.
        const diff = now >= start ? now - start : start - now + 1e6;
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIndex = i;
        }
      }
      return bestIndex;
    },

    activeAlertKey() {
      const idx = this.activeAlertIndex;
      return idx >= 0 ? this.groupedAlerts[idx]?.key || null : null;
    },
    currentOnlyAlert() {
      const alerts = this.groupedAlerts;
      if (this.activeAlertIndex >= 0) return alerts[this.activeAlertIndex];

      // The "current gesture" panel only surfaces real alerts; groups made of
      // discarded (non-alert) rows are skipped.
      const candidates = alerts.filter((a) => a.hasAlert);
      if (!candidates.length) return null;

      const now = Number(this.currentVideoTime);
      if (!Number.isFinite(now)) return candidates[0];

      let nearest = candidates[0];
      let minDiff = Number.POSITIVE_INFINITY;
      for (let i = 0; i < candidates.length; i += 1) {
        const diff = Math.abs(Number(candidates[i].seconds) - now);
        if (diff < minDiff) {
          minDiff = diff;
          nearest = candidates[i];
        }
      }
      return nearest;
    },
  },

  watch: {
    activeAlertIndex(next, prev) {
      if (next < 0 || next === prev) return;
      this.$emit('active-gesture-alert-change', {
        index: next,
        key: this.groupedAlerts[next]?.key || null,
        seconds: this.groupedAlerts[next]?.seconds ?? null,
      });
      // Skip auto-scroll when the active alert falls back to an earlier one
      // (e.g. a long-duration alert that "re-activates" after a shorter
      // overlapping alert ends). Manual seeks emit their own scroll path.
      if (prev != null && prev >= 0 && next < prev) return;
      this.scrollToActiveAlert(next);
    },
    reasonFilters: {
      deep: true,
      handler(value) { savePersistedFilters('reasonFilters.v2', value); },
    },
    speakingFilters: {
      deep: true,
      handler(value) { savePersistedFilters('speakingFilters.v2', value); },
    },
    methodFilters: {
      deep: true,
      handler(value) { savePersistedFilters('methodFilters.v2', value); },
    },
    discardedFilters: {
      deep: true,
      handler(value) { savePersistedFilters('discardedFilters.v2', value); },
    },
    sourceType() { this.loadExistingFeedback(); },
    sourceId() { this.loadExistingFeedback(); },
  },

  methods: {
    /** The settings menu keeps itself open on content clicks; close it before the dialog. */
    openTypologyFromMenu() {
      this.settingsMenuOpen = false;
      this.typologyDialogOpen = true;
    },

    normalizeGestureType(rawType, magnitude = null) {
      const value = String(rawType || '').trim().toLowerCase();
      if (value === 'gesture' || value === 'alert' || value === 'red') {
        return 'gesture';
      }
      if (value === 'sustained' || value === 'warning' || value === 'orange') {
        return 'sustained';
      }
      const numericMagnitude = Number(magnitude);
      if (Number.isFinite(numericMagnitude)) {
        return numericMagnitude >= 80 ? 'gesture' : 'sustained';
      }
      return 'sustained';
    },

    parseTimeToSeconds(timeString) {
      if (!timeString) return 0;
      const parts = String(timeString).split(':');
      if (parts.length !== 3) return 0;
      const minutes = parseInt(parts[0], 10) || 0;
      const seconds = parseInt(parts[1], 10) || 0;
      const milliseconds = parseInt(parts[2], 10) || 0;
      return minutes * 60 + seconds + milliseconds / 1000;
    },

    formatTimeClock(seconds) {
      if (!Number.isFinite(seconds) || seconds < 0) return '00:00:000';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      const ms = Math.floor((seconds % 1) * 1000);
      return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(ms).padStart(3, '0')}`;
    },

    formatDuration(ms) {
      const n = Number(ms);
      if (!Number.isFinite(n) || n <= 0) return '';
      if (n < 1000) return `${Math.round(n)}ms`;
      const secs = n / 1000;
      if (secs < 10) return `${secs.toFixed(1)}s`;
      return `${Math.round(secs)}s`;
    },

    /** Compact habituality chip, e.g. 'Absolute · rare' / 'Baseline · above baseline'. */
    subMethodChip(sub) {
      return methodChipLabel(sub);
    },

    subMethodChipColor(sub) {
      if (sub?.analysis_method === 'absolute') {
        const band = String(sub?.frequency_band || '').toLowerCase();
        if (band === 'habitual') return 'blue-grey darken-1';
        if (band === 'intermediate') return 'amber darken-3';
        return 'orange darken-2';
      }
      return sub?.reason === 'at_baseline' ? '#ffd60a' : 'orange darken-2';
    },

    subMethodChipDark(sub) {
      if (sub?.analysis_method === 'absolute') return true;
      return sub?.reason !== 'at_baseline';
    },

    /**
     * Compact status badge for a sub-row: 'Alert' for active alerts, the
     * human discard reason for non-alerts ('Habitual', 'At baseline',
     * 'Sustained — not valid', 'Suppressed while speaking'). When the method
     * chip already carries the same reason (e.g. 'Absolute · habitual'), the
     * badge falls back to 'Not an alert' to avoid repeating it. Legacy rows
     * (schema 1.3, no method data) show no badge so old analyses render
     * exactly as before.
     */
    subStatusBadge(sub) {
      if (sub?.has_method_data === false) return null;
      if (sub?.analysis_status === 'warning') return 'Warning';
      if (sub?.analysis_status !== 'discarded') return 'Alert';
      const reason = String(sub?.discard_reason || '').toLowerCase();
      const chip = String(this.subMethodChip(sub) || '').toLowerCase();
      const redundant =
        (reason === 'habitual' && chip.includes('habitual')) ||
        (reason === 'at_baseline' && chip.includes('at baseline'));
      return redundant ? 'Not an alert' : discardReasonLabel(sub?.discard_reason);
    },

    /** Tooltip for absolute rows carrying the per-video episode count. */
    subMethodTooltip(sub) {
      if (sub?.analysis_method !== 'absolute') return null;
      const n = Number(sub?.absolute_count);
      if (sub?.absolute_count == null || !Number.isFinite(n)) return null;
      return `Seen ${n} ${n === 1 ? 'time' : 'times'} in this video`;
    },

    subAlertRows(sub) {
      if (!sub) return [];

      const LABELS = {
        episode_id: 'Episode ID',
        gesture_name: 'Gesture',
        gesture_kind: 'Kind',
        reason: 'Reason',
        analysis_method: 'Analysis method',
        analysis_status: 'Analysis status',
        discard_reason: 'Discard reason',
        frequency_band: 'Frequency band',
        absolute_count: 'Absolute count',
        is_speaking: 'While speaking',
        duration_ms: 'Duration',
        confidence: 'Confidence',
        in_baseline: 'In baseline',
        overlaps_subject_speech: 'Overlaps subject speech',
        overlaps_interviewer_speech: 'Overlaps interviewer speech',
        local_freq: 'Local frequency (Hz)',
        global_freq: 'Global frequency (Hz)',
        local_episode_count: 'Local episode count',
        suppressed_reason: 'Suppressed',
        notes: 'Notes',
        time: 'Time',
        start_ms: 'Start (ms)',
        end_ms: 'End (ms)',
        start: 'Start',
        end: 'End',
        baseline_freq: 'Baseline frequency (Hz)',
        baseline_count: 'Baseline count',
        magnitude: 'Magnitude',
        score: 'Score',
        threshold: 'Threshold',
        z_score: 'Z-score',
        source: 'Source',
        model: 'Model',
        track_id: 'Track ID',
        hand: 'Hand',
      };

      const DESCRIPTIONS = {
        episode_id:
          'Unique identifier of the gesture episode. Format: "type:track:index" — grouped from consecutive frames of the same gesture type.',
        gesture_name:
          'Name of the detected gesture, as reported by the gesture classifier (e.g. gaze_deviation, emblem_mouth_twist_right).',
        gesture_kind:
          '"spontaneous" if the gesture happened while the subject was speaking or in silence; "reaction" if it happened while the interviewer was speaking.',
        reason:
          'Why this alert was triggered by the rule (e.g. "below" = value fell under the neutral threshold).',
        analysis_method:
          'Habituality method used for this gesture: "Absolute" classifies by total episode count in the video (frequency bands); "Baseline" compares against the subject\'s personal baseline period.',
        analysis_status:
          'Whether the row is an active alert or was discarded. Discarded rows are shown for transparency but are never part of the analysis.',
        discard_reason:
          'Why the row was discarded: sustained episode not valid for the gesture, habitual gesture (the person\'s tic), no deviation from baseline, or suppressed while the subject was speaking.',
        frequency_band:
          'Absolute-method frequency band of this gesture in the video: rare, intermediate, or habitual. Bands only gate/label severity.',
        absolute_count:
          'Total number of episodes of this gesture detected in the whole video (absolute method only).',
        is_speaking:
          'Whether the subject was actively speaking at the moment of the gesture (derived from diarization).',
        duration_ms:
          'Episode duration in milliseconds (end_time_ms − start_time_ms).',
        confidence:
          'Classifier confidence in the detection (0–100%). Empty when the detector does not report a confidence score (e.g. gaze).',
        in_baseline:
          'Whether this episode falls inside the baseline window (the neutral period at the start of the interview used as the subject\'s personal reference).',
        overlaps_subject_speech:
          'Whether the episode time range overlaps an interval where the subject was speaking, according to speaker diarization.',
        overlaps_interviewer_speech:
          'Whether the episode time range overlaps an interval where the interviewer was speaking, according to speaker diarization.',
        local_freq:
          'Frequency of this gesture in the local window around the episode, in Hertz (episodes per second).',
        global_freq:
          'Frequency of this gesture across the entire interview, in Hertz (episodes per second).',
        local_episode_count:
          'Number of episodes of this gesture inside the local window around this one.',
        baseline_freq:
          'Reference frequency of this gesture during the subject\'s baseline period. Empty if no baseline is configured.',
        baseline_count:
          'Number of episodes of this gesture detected during the baseline period.',
        magnitude:
          'Intensity of the gesture as reported by the detector (e.g. how far the gaze deviates). Scale depends on the detector.',
        score:
          'Composite rule score — usually combines frequency, magnitude and baseline deviation. Empty for rules that only use a magnitude threshold.',
        threshold:
          'Score threshold required by the rule to fire the alert.',
        z_score:
          'Standard deviations away from the baseline mean. Empty when no baseline is available.',
        suppressed_reason:
          'Reason the alert was hidden by automatic suppression (e.g. "subject_speech" for mouth gestures that overlap subject speech).',
        feature_weights:
          'Per-feature weights used by the rule\'s scoring model. Empty for rules that are not weighted.',
        notes:
          'Free-form notes attached by the engine to this episode (rarely populated).',
        time: 'Timestamp inside the video where the alert starts (mm:ss).',
        start_ms: 'Episode start time inside the video, in milliseconds.',
        end_ms: 'Episode end time inside the video, in milliseconds.',
        start: 'Episode start time inside the video.',
        end: 'Episode end time inside the video.',
        source: 'Subsystem that produced the detection (e.g. mediapipe, sagemaker).',
        model: 'Specific model that produced the detection.',
        track_id: 'Identifier of the tracked person/body across frames.',
        hand: 'Which hand produced the gesture (left / right), when applicable.',
      };

      const humanize = (key) =>
        LABELS[key] ||
        String(key)
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (m) => m.toUpperCase());

      const fmtValue = (key, value) => {
        if (value === null || value === undefined || value === '') return null;
        if (key === 'analysis_method') {
          return String(value).toLowerCase() === 'absolute' ? 'Absolute' : 'Baseline';
        }
        if (key === 'analysis_status') {
          const status = String(value).toLowerCase();
          if (status === 'warning') return 'Warning';
          return status === 'discarded' ? 'Discarded' : 'Alert';
        }
        if (key === 'discard_reason') return discardReasonLabel(value);
        if (key === 'frequency_band') {
          const band = String(value).trim();
          return band ? band.charAt(0).toUpperCase() + band.slice(1).toLowerCase() : null;
        }
        if (typeof value === 'boolean') return value ? 'Yes' : 'No';
        if (typeof value === 'number') {
          if (!Number.isFinite(value)) return null;
          if (key === 'confidence' && value >= 0 && value <= 1) {
            return `${Math.round(value * 100)}%`;
          }
          if (key === 'duration_ms') return this.formatDuration(value) || `${Math.round(value)} ms`;
          if (Number.isInteger(value)) return String(value);
          return Number(value.toFixed(4)).toString();
        }
        if (typeof value === 'string') return value;
        if (Array.isArray(value)) {
          if (!value.length) return null;
          return value
            .map((x) => (x && typeof x === 'object' ? JSON.stringify(x) : String(x)))
            .join(', ');
        }
        if (typeof value === 'object') {
          try { return JSON.stringify(value); } catch (e) { return String(value); }
        }
        return String(value);
      };

      // Stable schema: always show the same rows across every gesture so the
      // detail dialog has a consistent shape. Missing fields render as "N/A"
      // instead of being hidden — easier to scan and prevents the layout from
      // jumping between detection types (gaze, audio, manipulators, ...).
      const GROUPS = [
        {
          title: 'Information',
          keys: [
            'episode_id',
            'gesture_name',
            'reason',
            'analysis_method',
            'analysis_status',
            'discard_reason',
          ],
        },
        {
          title: 'Context',
          keys: [
            'gesture_kind',
            'is_speaking',
            'overlaps_subject_speech',
            'overlaps_interviewer_speech',
          ],
        },
        { title: 'Timing', keys: ['duration_ms'] },
        {
          title: 'Metrics',
          keys: [
            'frequency_band',
            'absolute_count',
            'local_freq',
            'global_freq',
            'local_episode_count',
            'baseline_freq',
            'baseline_count',
            'in_baseline',
          ],
        },
        {
          title: 'Score',
          keys: ['magnitude', 'confidence', 'score', 'threshold', 'z_score', 'feature_weights'],
        },
        { title: 'Others', keys: ['suppressed_reason', 'notes'] },
      ];
      const CANONICAL_ORDER = GROUPS.flatMap((g) => g.keys);
      // Method fields only exist for new analyses (or specific methods); when
      // absent we hide the row instead of rendering a noisy "N/A".
      const OPTIONAL_DETAIL_KEYS = new Set(['frequency_band', 'absolute_count', 'discard_reason']);

      const available = new Map(); // canonicalKey → formatted value
      const extras = [];           // { label, value } for unknown keys
      const seenLabels = new Set();

      const collect = (obj, skip) => {
        if (!obj || typeof obj !== 'object') return;
        Object.keys(obj).forEach((key) => {
          if (skip && skip.has(key)) return;
          const formatted = fmtValue(key, obj[key]);
          if (formatted == null || formatted === '') return;
          if (CANONICAL_ORDER.includes(key)) {
            if (!available.has(key)) available.set(key, formatted);
            return;
          }
          const label = humanize(key);
          if (seenLabels.has(label)) return;
          seenLabels.add(label);
          extras.push({ label, value: formatted });
        });
      };

      // 1. Normalized top-level fields on the sub-alert.
      collect(sub, new Set(['id', 'explain', 'raw', 'has_method_data']));
      // 2. Backend `explain` bag — the primary metadata payload.
      collect(sub.explain, null);
      // 3. Any other field the raw backend alert ships at the top level.
      if (sub.raw && typeof sub.raw === 'object') {
        const knownTop = new Set([
          'episode_id', 'gesture_name', 'gesture_kind', 'reason',
          'is_speaking', 'duration_ms', 'explain', 'has_method_data',
        ]);
        collect(sub.raw, knownTop);
      }

      const groups = GROUPS.map((g) => ({
        title: g.title,
        rows: g.keys
          .map((key) => ({
            key,
            label: humanize(key),
            value: available.has(key) ? available.get(key) : 'N/A',
            description: DESCRIPTIONS[key] || '',
          }))
          .filter((row) => !(OPTIONAL_DETAIL_KEYS.has(row.key) && row.value === 'N/A')),
      }));
      // Append any backend fields outside the canonical schema so new
      // metadata still surfaces without requiring a frontend change.
      if (extras.length) {
        const others = groups.find((g) => g.title === 'Others');
        const extraRows = extras.map((e) => ({ ...e, description: e.description || '' }));
        if (others) others.rows.push(...extraRows);
        else groups.push({ title: 'Others', rows: extraRows });
      }
      return groups;
    },
    openDetailDialog(alert) {
      this.detailAlert = alert;
      // When there's a single detection, expand it by default — collapsing a
      // lone item would just add an extra click for no reason. With multiple
      // detections we start fully collapsed so the dialog stays compact.
      const subCount = Array.isArray(alert?.subAlerts) ? alert.subAlerts.length : 0;
      this.detailExpandedPanels = subCount === 1 ? [0] : [];
      this.detailDialogOpen = true;
    },
    seekFromDetailDialog() {
      if (this.detailAlert) {
        this.$emit('jump-to-time', this.detailAlert.seconds);
      }
      this.detailDialogOpen = false;
    },
    onAlertActivate(alert) {
      if (this.reportMissingActive) {
        this.captureMissingTimestamp(alert.seconds);
        return;
      }
      if (this.pickModeEnabled) {
        this.$emit('pick-timestamp', alert.seconds);
        return;
      }
      this.$emit('jump-to-time', alert.seconds);
    },

    /**
     * Derived state for a card: aggregates the verdicts of its sub-alerts.
     * - All sub-alerts marked correct → 'correct'
     * - Any sub-alert marked incorrect → 'incorrect'
     * - Otherwise null (no highlight)
     */
    cardVerdict(alert) {
      const subs = Array.isArray(alert?.subAlerts) ? alert.subAlerts : [];
      if (!subs.length) return this.gestureFeedback[alert.key] || null;
      let allCorrect = true;
      let anyIncorrect = false;
      let anyMarked = false;
      for (const sub of subs) {
        const v = this.gestureFeedback[sub.id];
        if (v === 'incorrect') {
          anyIncorrect = true;
          anyMarked = true;
        } else if (v === 'correct') {
          anyMarked = true;
        } else {
          allCorrect = false;
        }
      }
      if (anyIncorrect) return 'incorrect';
      if (anyMarked && allCorrect) return 'correct';
      return null;
    },

    /**
     * Hydrate `gestureFeedback` from previously stored detection_feedback rows
     * so the user sees their past votes (correct/incorrect) when reopening a
     * video. Without this the UI starts empty even if the backend has labels.
     */
    async loadExistingFeedback() {
      if (!this.sourceType || !this.sourceId) return;
      try {
        const { data } = await DetectionFeedbackService.list(this.sourceType, this.sourceId);
        const items = Array.isArray(data?.data?.items) ? data.data.items : [];
        const next = {};
        for (const it of items) {
          if (it.target_type !== 'gesture') continue;
          if (!it.target_ref) continue;
          if (it.verdict === 'correct' || it.verdict === 'incorrect') {
            next[it.target_ref] = it.verdict;
          }
        }
        this.gestureFeedback = next;
      } catch (e) {
        // silent
      }
    },

    async markGesture(alert, verdict) {
      if (!this.sourceType || !this.sourceId) {
        // Without a source we cannot persist; fall back to in-memory state only.
        this.$set(this.gestureFeedback, alert.key, verdict);
        return;
      }
      const subs = Array.isArray(alert.subAlerts) ? alert.subAlerts : [];
      const singleGestureName = subs.length === 1 ? subs[0]?.gesture_name : null;
      const singleEpisodeId = subs.length === 1 ? (subs[0]?.episode_id || null) : null;
      const stableGestureId = singleEpisodeId || String(alert.key);

      if (verdict === 'correct') {
        // Single-sub card → one feedback row keyed by its episode_id.
        // Multi-sub card → one feedback row PER sub-alert so each gesture in
        // the group inherits the "correct" verdict and the aggregated view
        // reflects it consistently.
        const cardStartMs = Math.round(Number(alert.seconds) * 1000);
        const targets = subs.length
          ? subs.map((s) => ({
              id: s.id,
              name: s.gesture_name || null,
              startMs: s.start_ms ?? cardStartMs,
              endMs: s.end_ms ?? cardStartMs,
            }))
          : [{ id: stableGestureId, name: singleGestureName || null, startMs: cardStartMs, endMs: cardStartMs }];
        try {
          await Promise.all(
            targets.map((t) =>
              DetectionFeedbackService.submitGestureFeedback({
                sourceType: this.sourceType,
                sourceId: Number(this.sourceId),
                gestureId: String(t.id),
                gestureName: t.name,
                startMs: t.startMs,
                endMs: t.endMs,
                verdict: 'correct',
              })
            )
          );
          for (const t of targets) {
            this.$set(this.gestureFeedback, String(t.id), 'correct');
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('[GestureAlertsTimeline] mark correct failed', e);
        }
        return;
      }
      // For incorrect, open dialog. If the card aggregates multiple gestures,
      // surface them as sub-alerts so the analyst can mark each one — same
      // pattern as the idea_alert review flow.
      this.feedbackTarget = {
        id: stableGestureId,
        label: alert.description,
        gesture_name: singleGestureName || null,
        timestamp_ms: Math.round(Number(alert.seconds) * 1000),
        start_ms: subs.length === 1 ? subs[0]?.start_ms ?? null : null,
        end_ms: subs.length === 1 ? subs[0]?.end_ms ?? null : null,
      };
      this.feedbackAlerts = subs.length > 1
        ? subs.map((s) => ({
            id: s.id,
            rule_label: s.gesture_name,
            gesture_name: s.gesture_name,
            timestamp_ms: Math.round(Number(alert.seconds) * 1000),
            start_ms: s.start_ms ?? null,
            end_ms: s.end_ms ?? null,
            explain: s.explain,
          }))
        : [];
      this.feedbackDialogOpen = true;
    },

    onGestureFeedbackSaved(payload) {
      // Patch local state immediately for snappy UI: one entry per persisted
      // verdict, keyed by the same target_ref the backend used.
      const verdicts = payload && payload.verdicts ? payload.verdicts : null;
      if (verdicts) {
        for (const [key, value] of Object.entries(verdicts)) {
          if (value === 'correct' || value === 'incorrect') {
            this.$set(this.gestureFeedback, String(key), value);
          }
        }
      }
      this.feedbackTarget = null;
      this.feedbackAlerts = [];
      // Re-sync from backend (source of truth) so future state matches DB.
      this.loadExistingFeedback();
    },

    onTrackBackgroundClick(event) {
      if (!this.pickActive) return;
      const track = this.$refs.timelineTrack;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      if (rect.width <= 0) return;
      const x = Math.max(0, Math.min(rect.width, event.clientX - rect.left));
      const duration = this.effectiveDuration;
      if (!Number.isFinite(duration) || duration <= 0) return;
      const seconds = (x / rect.width) * duration;
      if (this.reportMissingActive && !this.pickModeEnabled) {
        this.captureMissingTimestamp(seconds);
        return;
      }
      this.$emit('pick-timestamp', seconds);
    },

    startReportMissing() {
      if (this.reportMissingActive) {
        // Toggle off
        this.reportMissingActive = false;
        this.$emit('report-missing-cancel');
        return;
      }
      this.reportMissingActive = true;
      this.missingPendingTimestampMs = null;
      this.$emit('report-missing-start');
    },

    captureMissingTimestamp(seconds) {
      this.missingPendingTimestampMs = Math.round(Number(seconds) * 1000);
      this.missingDialogOpen = true;
      this.$emit('report-missing-captured', this.missingPendingTimestampMs);
    },

    onMissingSaved() {
      this.reportMissingActive = false;
      this.missingPendingTimestampMs = null;
      this.missingDialogOpen = false;
      this.$emit('report-missing-saved');
    },

    onMissingCancel() {
      this.reportMissingActive = false;
      this.missingPendingTimestampMs = null;
      this.missingDialogOpen = false;
      this.$emit('report-missing-cancel');
    },

    scrollToActiveAlert(index) {
      this.$nextTick(() => {
        const refs = this.$refs.alertItemEl;
        const items = Array.isArray(refs) ? refs : refs ? [refs] : [];
        const el = items[index];
        const container = this.$refs.alertListScroll;
        if (!el || !container) return;
        const targetTop = el.offsetTop - container.clientHeight / 2 + el.offsetHeight / 2 - 60;
        const maxTop = Math.max(0, container.scrollHeight - container.clientHeight);
        const nextTop = Math.max(0, Math.min(maxTop, targetTop));
        container.scrollTo({ top: nextTop, behavior: 'smooth' });
      });
    },
  },
};
</script>

<style scoped>
.gesture-alerts-card {
  border-top: 3px solid #6200ea;
  min-width: 300px;
  max-width: 320px;
  width: 100%;
  min-height: 0;
  overflow: hidden;
  background: rgba(5, 6, 9, 0.1) !important;
}

@supports ((-webkit-backdrop-filter: blur(1px)) or (backdrop-filter: blur(1px))) {
  .gesture-alerts-card {
    background: rgba(4, 5, 8, 0.9) !important;
    -webkit-backdrop-filter: saturate(140%) blur(22px);
    backdrop-filter: saturate(140%) blur(22px);
  }
}

.gesture-card-title-line {
  min-width: 0;
  width: 100%;
}

.gesture-card-title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gesture-meta {
  color: rgba(235, 236, 240, 0.58);
}

.gesture-meta--ellipsis {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gesture-timeline-track {
  position: relative;
  height: 44px;
}

.gesture-timeline-legend {
  color: rgba(240, 242, 248, 0.85);
  row-gap: 4px;
}

.gesture-legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.gesture-legend-dot--alerted { background: #ff9800; }
.gesture-legend-dot--baseline { background: #ffd60a; }
.gesture-legend-dot--muted {
  width: 8px;
  height: 8px;
  background: rgba(158, 163, 178, 0.7);
}

.gesture-timeline-footer {
  display: flex;
  align-items: center;
}

.gesture-timeline-track--picking {
  cursor: crosshair;
  outline: 1px dashed rgba(255, 193, 7, 0.6);
  outline-offset: 2px;
}

.gesture-timeline-line {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  transform: translateY(-50%);
  background: rgba(228, 231, 238, 0.3);
}

.gesture-marker-btn {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
}

.gesture-marker-dot {
  display: block;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.35);
}

.gesture-marker-btn--error .gesture-marker-dot,
.gesture-marker-btn--alerted .gesture-marker-dot {
  background: #ff9800;
}

.gesture-marker-btn--warning .gesture-marker-dot,
.gesture-marker-btn--baseline .gesture-marker-dot {
  background: #ffd60a;
}

.gesture-marker-btn--success .gesture-marker-dot {
  background: #4caf50;
}

/* Non-alert (discarded) detections: smaller, dim grey, behind alert markers. */
.gesture-marker-btn--error,
.gesture-marker-btn--warning,
.gesture-marker-btn--alerted,
.gesture-marker-btn--baseline {
  z-index: 1;
}

.gesture-marker-btn--muted {
  z-index: 0;
}

.gesture-marker-btn--muted .gesture-marker-dot {
  width: 8px;
  height: 8px;
  background: rgba(158, 163, 178, 0.55);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.18);
}

.gesture-marker-btn--active .gesture-marker-dot {
  transform: scale(1.25);
  box-shadow:
    0 0 0 3px rgba(100, 181, 246, 0.36),
    0 0 0 1px rgba(255, 255, 255, 0.7);
}

.gesture-alerts-scroll {
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
}

.gesture-alerts-scroll::-webkit-scrollbar {
  width: 0;
  height: 0;
}

.gesture-empty {
  color: rgba(232, 233, 238, 0.5);
}

.gesture-alerts-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gesture-alert-item {
  text-align: left;
  border: 2px solid transparent;
  background: rgba(255, 255, 255, 0.04);
  color: rgba(240, 242, 248, 0.9);
  border-radius: 10px;
  padding: 10px 12px;
  transition: background-color 0.2s ease, border-color 0.2s ease;
}

.gesture-alert-item__header {
  display: flex;
  align-items: center;
  gap: 8px;
}

.gesture-alert-item__timing {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;
}

.gesture-alert-item__body {
  margin-top: 8px;
  font-size: 13px;
  color: rgba(240, 242, 248, 0.92);
  cursor: pointer;
  outline: none;
  line-height: 1.35;
}

.gesture-alert-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.15);
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

.gesture-alert-signal-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 2px 0;
}

.gesture-alert-signal-name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Discarded (non-alert) sub-rows read dimmer than their alert siblings. */
.gesture-alert-signal-row--muted {
  opacity: 0.6;
}

.gesture-status-badge {
  flex-shrink: 0;
}

.gesture-alert-item__footer {
  display: flex;
  gap: 6px;
  margin-top: 10px;
}

/* Discarded gestures section: transparent rows, never part of the analysis */
.gesture-discarded-section {
  border-top: 1px solid rgba(255, 255, 255, 0.12);
  padding-top: 6px;
}

.gesture-discarded-toggle {
  background: transparent;
  border: 0;
  padding: 2px 0;
  cursor: pointer;
  color: rgba(235, 236, 240, 0.72);
  width: 100%;
  text-align: left;
}

.gesture-discarded-toggle:hover {
  color: rgba(240, 242, 248, 0.95);
}

.gesture-discarded-summary {
  gap: 4px;
}

.gesture-discarded-hint {
  color: rgba(235, 236, 240, 0.55);
}

.gesture-discarded-badge {
  flex-shrink: 0;
  opacity: 0.8;
}

/* Debug actions (legacy labeling): removed from the layout until the
   containing card is hovered (no reserved gap in the card), and only on
   devices that support hover. */
.debug-actions {
  display: none;
}

@media (hover: hover) {
  .gesture-alert-item:hover .debug-actions,
  .gesture-alert-item:focus-within .debug-actions {
    display: flex;
  }
}


.gesture-vote-btn {
  flex: 1;
  font-size: 11px !important;
  letter-spacing: 0.02em;
  text-transform: none !important;
  opacity: 0.7;
  transition: opacity 0.15s, background-color 0.15s;
}

.gesture-vote-btn--active-correct,
.gesture-vote-btn--active-incorrect {
  opacity: 1 !important;
  color: #fff !important;
}

.gesture-vote-btn--inactive:hover {
  opacity: 1;
}

.gesture-alert-item__info-btn {
  opacity: 0.6;
  transition: opacity 0.15s ease;
}

.gesture-alert-item__info-btn:hover {
  opacity: 1;
}

.gesture-detail-dialog-card {
  background: #fff;
}

.gesture-detail-sub-card {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: #fafafa;
}

.gesture-detail-sub-card:last-child {
  margin-bottom: 0;
}

.gesture-detail-panels {
  background: transparent;
}

.gesture-detail-sub-panel {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px !important;
  margin-bottom: 8px;
  background: #fafafa;
}

.gesture-detail-sub-panel:last-child {
  margin-bottom: 0;
}

.gesture-detail-sub-panel::before {
  box-shadow: none !important;
}

.gesture-detail-sub-header {
  min-height: 44px;
}

.gesture-detail-sub-content :deep(.v-expansion-panel-content__wrap) {
  padding: 0 16px 12px;
}

.alert-detail-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px;
}

.alert-detail-group-title {
  grid-column: 1 / -1;
  margin-top: 10px;
  padding-bottom: 2px;
  color: #888;
  letter-spacing: 0.08em;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.alert-detail-group-title:first-child {
  margin-top: 0;
}

.alert-detail-row {
  display: grid;
  grid-template-columns: 160px 1fr;
  column-gap: 12px;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.alert-detail-row:last-child {
  border-bottom: none;
}

.alert-detail-label {
  font-weight: 600;
  color: #555;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.alert-detail-info {
  color: #999 !important;
  cursor: help;
  opacity: 0.8;
}

.alert-detail-info:hover,
.alert-detail-info:focus {
  color: #555 !important;
  opacity: 1;
  outline: none;
}

.alert-detail-value {
  color: #212121;
  word-break: break-word;
}

/* Active in time: border in the card's own tone color. */
.gesture-alert-item--warning.gesture-alert-item--active,
.gesture-alert-item--baseline.gesture-alert-item--active {
  border-color: rgba(255, 214, 10, 0.9);
}

.gesture-alert-item--error.gesture-alert-item--active,
.gesture-alert-item--alerted.gesture-alert-item--active {
  border-color: rgba(255, 152, 0, 0.92);
}

/* Labeled overrides the active border color (background untouched). */
.gesture-alert-item--active.gesture-alert-item--feedback-correct {
  border-color: rgba(76, 175, 80, 0.95);
}

.gesture-alert-item--active.gesture-alert-item--feedback-incorrect {
  border-color: rgba(244, 67, 54, 0.95);
}

.gesture-alert-item--warning,
.gesture-alert-item--baseline {
  background: rgba(255, 214, 10, 0.12);
}

.gesture-alert-item--error,
.gesture-alert-item--alerted {
  background: rgba(255, 152, 0, 0.14);
}

/* Groups with no alert rows (every detection discarded): muted grey tone. */
.gesture-alert-item--muted {
  background: rgba(255, 255, 255, 0.03);
  opacity: 0.78;
}

.gesture-alert-duration {
  font-size: 10px;
  color: rgba(240, 242, 248, 0.65);
  font-variant-numeric: tabular-nums;
}

.gesture-alert-reason-chip {
  text-transform: uppercase;
  font-size: 9px !important;
  letter-spacing: 0.04em;
}

.gesture-menu {
  border-radius: 8px;
}

.gesture-alert-item__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.gesture-alert-time {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0;
}

.gesture-alert-description {
  margin-top: 5px;
  font-size: 11px;
  line-height: 1.35;
  color: rgba(240, 242, 248, 0.92);
  word-break: break-word;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 4;
  overflow: hidden;
}

.gesture-alert-description--current-only {
  -webkit-line-clamp: 2;
  min-height: calc(11px * 1.35 * 2);
  max-height: calc(11px * 1.35 * 2);
}
</style>
